// import { clearSlice } from "utils/HelperFunctions";
import axios, { AxiosError, AxiosResponse } from "axios";
import {
  updateTokenExpiredStatus,
  // updateTokenStatus,
} from "store/slices/authSlice";
import { store } from "../store";
import {
  BASE_URL,
  DOWNLOAD_PAYMENT,
  GET_PAYMENT_RECEIPT,
} from "./constApi";

export const TOKEN_STORAGE_KEY = "dcdial-customer-token";
export const TOKEN_STORAGE_TIME = "dcdial-customer-token-time";
// const INVALID_TOKEN_MESSAGE = "Invalid token or expired.";
const UNAUTHORIZED_STATUS = "Unauthorized";
const FORBIDDEN_STATUS = "Forbidden";

export const apiInstance = axios.create({
  baseURL: BASE_URL,
});

// function resolveBaseUrl(requestUrl?: string): string {
//   if (!requestUrl) {
//     return BASE_URL;
//   }

//   if (requestUrl.startsWith("/payment")) {
//     return PAYMENT_URL;
//   }

  // if (requestUrl.startsWith("/portal")) {
  //   return PORTAL_URL;
  // }

  // if (requestUrl.startsWith("/contact")) {
  //   return CONTACT_URL;
  // }

//   return BASE_URL;
// }

// A successful payment response carries a short-lived receipt token for its
// order. Sending it along with the receipt request shows the email API that
// this browser made the payment, so the receipt may go to the address the
// payer entered.
const RECEIPT_TOKENS_KEY = "dcdial-receipt-tokens";

function readReceiptTokens(): Record<string, string> {
  try {
    return JSON.parse(sessionStorage.getItem(RECEIPT_TOKENS_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function rememberReceiptTokens(tokens: unknown): void {
  if (!tokens || typeof tokens !== "object") return;
  try {
    sessionStorage.setItem(
      RECEIPT_TOKENS_KEY,
      JSON.stringify({ ...readReceiptTokens(), ...(tokens as object) })
    );
  } catch {
    // Storage is unavailable; the receipt still goes to the address on file.
  }
}

// A call that creates a stored payment method (a typed card, a linked bank
// account, PayPal) hands back a short-lived method token for it. A visitor
// who is not logged in may pay with a stored method only when the request
// carries its token, so every payment that names a method sends the ones
// this browser holds.
const METHOD_TOKENS_KEY = "dcdial-method-tokens";
const MAX_METHOD_TOKENS = 10;

function readMethodTokens(): string[] {
  try {
    const stored = JSON.parse(sessionStorage.getItem(METHOD_TOKENS_KEY) || "[]");
    if (!Array.isArray(stored)) return [];
    const now = Date.now();
    // "<method id>.<expiry in ms>.<signature>"
    return stored.filter(
      (token) => typeof token === "string" && Number(token.split(".")[1]) > now
    );
  } catch {
    return [];
  }
}

function rememberMethodTokens(tokens: unknown): void {
  if (!Array.isArray(tokens)) return;
  try {
    const all = [
      ...readMethodTokens(),
      ...tokens.filter((token) => typeof token === "string"),
    ];
    sessionStorage.setItem(
      METHOD_TOKENS_KEY,
      JSON.stringify(Array.from(new Set(all)).slice(-MAX_METHOD_TOKENS))
    );
  } catch {
    // Storage is unavailable; a method created moments ago is still accepted.
  }
}

function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

apiInstance.interceptors.request.use(
  (config) => {
    // A request that carries its own token keeps it: the password forms send
    // the one-time token from OTP verification, before the customer has a
    // session token in storage.
    if (!config.headers.Authorization) {
      const token = getAuthToken();
      config.headers.Authorization = token ? `Bearer ${token}` : null;
    }
    config.headers["ngrok-skip-browser-warning"] = true;
    if (
      config.url === DOWNLOAD_PAYMENT &&
      config.data &&
      typeof config.data === "object" &&
      !Array.isArray(config.data) &&
      !config.data.receipt_token
    ) {
      const orderId = config.data.invoice_id ?? config.data.schedule_id;
      const receiptToken = readReceiptTokens()[String(orderId)];
      if (receiptToken) {
        config.data = { ...config.data, receipt_token: receiptToken };
      }
    }
    if (
      config.data &&
      typeof config.data === "object" &&
      !Array.isArray(config.data) &&
      !(config.data instanceof FormData) &&
      (config.data.cppid || config.data.cpp_id || config.data.cc_token) &&
      !config.data.method_tokens
    ) {
      const methodTokens = readMethodTokens();
      if (methodTokens.length > 0) {
        config.data = { ...config.data, method_tokens: methodTokens };
      }
    }
    // The same token lets this browser view the receipt of a payment it just
    // made on a page where nobody is logged in.
    if (
      config.url === GET_PAYMENT_RECEIPT &&
      config.params &&
      typeof config.params === "object" &&
      config.params.id &&
      !config.params.receipt_token
    ) {
      const receiptToken = readReceiptTokens()[String(config.params.id)];
      if (receiptToken) {
        config.params = { ...config.params, receipt_token: receiptToken };
      }
    }
    // config.baseURL = resolveBaseUrl(config.url);
    return config;
  },
  (error) => Promise.reject(error)
);

apiInstance.interceptors.response.use((response) => {
  rememberReceiptTokens(response?.data?.body?.data?.receipt_tokens);
  rememberMethodTokens(response?.data?.body?.data?.method_tokens);
  return response.data;
}, handleError);

function handleError(error: AxiosError): Promise<never> {
  if (error.response && isErrorResponse(error.response)) {
    handleAuthorizationErrors(error.response);
  }

  return Promise.reject(error);
}

function isErrorResponse(response: AxiosResponse): boolean {
  return (
    typeof response.data.message === "string" &&
    typeof response.data.status === "string"
  );
}

// The server marks a refusal that is about the request, not the session (a
// payment method that may not be used, a route that is not for customers).
// Those are left to the screen that made the call; showing "Session Timeout"
// for them would log the customer out for nothing.
const NOT_ALLOWED_CODE = "NOT_ALLOWED";

function handleAuthorizationErrors(response: AxiosResponse): void {
  // Middleware refusals carry the code at the top; a service's refusal comes
  // back inside `body` (quick checkout: "This account cannot be used").
  if (
    response.data?.code === NOT_ALLOWED_CODE ||
    response.data?.body?.code === NOT_ALLOWED_CODE
  ) {
    return;
  }
  if (response.status === 401 || response.data?.status === UNAUTHORIZED_STATUS) {
    // localStorage.removeItem(TOKEN_STORAGE_KEY);
    // clearSlice(store.dispatch);
    store.dispatch(updateTokenExpiredStatus(true));
  } else if (response.status === 403 || response.data?.status === FORBIDDEN_STATUS) {
    store.dispatch(updateTokenExpiredStatus(true));
  }
}
